import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Typography, 
  Slider, 
  TextField, 
  Grid, 
  Card, 
  CardContent,
  Box, 
  Paper,
  Divider,
  IconButton,
  Checkbox,
  InputAdornment,
  List,
  ListItem,  
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import html2canvas from 'html2canvas';
import CustomListItemText from '../components/CustomListItemText';
import NumberInputField from '../components/NumberInputField';
import ApiService from '../services/apiService';

// Custom Hook für Debounce-Funktion
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Hilfsfunktion zum Runden auf 2 Nachkommastellen
const roundToTwoDecimals = (value) => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

// Stilisierter Container für den Schieberegler
const SliderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: 0,
  marginTop: 0,
}));

// Stilisierte Kachel für die Gebührendarstellung
const FeeTile = styled(Card, {
  shouldForwardProp: (prop) => !['isCheapest', 'isNightowl', 'isOwnNightowl'].includes(prop)
})(({ theme, isCheapest, isNightowl, isOwnNightowl }) => ({
  width: '100%',
  position: 'relative',
  paddingBottom: '100%', /* Erzwingt ein quadratisches Format */
  display: 'block',
  transition: 'transform 0.3s ease',
  border: isCheapest 
    ? `2px solid var(--success)` 
    : isNightowl 
      ? isOwnNightowl 
        ? theme.palette.mode === 'dark'
          ? `2px dashed var(--primary-light)` // Gestrichelter Rahmen für aktuelle NIGHTOWL Gebühr im Dark Mode
          : `2px dashed var(--primary-main)` // Gestrichelter Rahmen für aktuelle NIGHTOWL Gebühr im Light Mode
        : theme.palette.mode === 'dark'
          ? `2px solid var(--primary-light)` // Normaler Rahmen für neue NIGHTOWL Gebühr im Dark Mode
          : `2px solid var(--primary-main)` // Normaler Rahmen für neue NIGHTOWL Gebühr im Light Mode
      : 'none',
  boxShadow: isCheapest 
    ? theme.shadows[8] 
    : isNightowl 
      ? theme.shadows[4]
      : theme.shadows[1],
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[5],
  },
}));

// Inhalt-Container für die Kachel (als absolut positioniertes Element)
const TileContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
}));

// Stilisierter Logo-Container
const LogoContainer = styled(Box, {
  shouldForwardProp: (prop) => !['isCheapest', 'isNightowl'].includes(prop)
})(({ theme, isCheapest, isNightowl }) => ({
  background: isCheapest 
    ? theme.palette.mode === 'dark' 
      ? 'var(--success-dark, var(--success))' 
      : 'var(--success-light, var(--success))'
    : isNightowl
      ? theme.palette.mode === 'dark'
        ? 'var(--primary-dark)'
        : 'var(--primary-dark)'
      : theme.palette.mode === 'dark' 
        ? 'var(--neutral-200)' 
        : 'var(--neutral-200)',
  padding: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '25%', // Höhe relativ zur Gesamthöhe der Kachel
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
}));

// Stilisierte Logo-Darstellung
const Logo = styled('img')(({ theme }) => ({
  maxWidth: '70%',
  maxHeight: '80%',
  objectFit: 'contain',
  [theme.breakpoints.down('md')]: {
    maxWidth: '65%',
  },
}));

// Stilisierter Paketname
const PackageName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isNightowl'
})(({ theme, isNightowl }) => ({
  textAlign: 'center',
  fontWeight: 'bold',
  padding: theme.spacing(0.5, 0),
  backgroundColor: isNightowl 
    ? theme.palette.mode === 'dark' 
      ? 'var(--primary-light)' 
      : 'var(--primary-light)'
    : theme.palette.mode === 'dark' 
      ? 'var(--neutral-300)' 
      : 'var(--neutral-200)',
  color: isNightowl 
    ? 'var(--neutral-100)' 
    : 'inherit',
  height: '18%' // Höhe relativ zur Gesamthöhe der Kachel
}));

// Stilisierter Preisbereich
const PriceContainer = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isCheapest'
})(({ theme, isCheapest }) => ({
  textAlign: 'center',
  fontSize: 'clamp(1rem, 2vw, 1.3rem)', // Responsive Schriftgröße
  fontWeight: 'bold',
  padding: theme.spacing(0.5, 0),
  height: '22%', // Höhe relativ zur Gesamthöhe der Kachel
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: isCheapest ? 'var(--success)' : 'inherit',
}));

// Stilisierter Details-Container
const DetailsContainer = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(0.5),
  height: '35%', // Höhe relativ zur Gesamthöhe der Kachel
  '& .MuiTypography-body2': {
    fontSize: 'clamp(0.65rem, 1.3vw, 0.8rem)', // Responsive Schriftgröße für Details
  }
}));

// Stilisierung für die Kachel-Ansicht in der neuen Version
// wird jetzt über inline-Styling implementiert

// Stilisierter Container für die Kacheln
const TilesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  padding: '0',
  margin: '0',
}));

// Stilisiertes Container für einzelne Kacheln
const TileWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(1, 1, 1, 1),
  boxSizing: 'border-box',
  [theme.breakpoints.up('xs')]: {
    width: '50%',  // 2 Kacheln pro Zeile bei xs
  },
  [theme.breakpoints.up('sm')]: {
    width: '50%',   // 2 Kacheln pro Zeile bei sm
  },
  [theme.breakpoints.up('md')]: {
    width: '33.33%', // 3 Kacheln pro Zeile bei md
  },
  [theme.breakpoints.up('lg')]: {
    width: '25%',   // 4 Kacheln pro Zeile bei lg und xl
  },
}));

// Stilkomponenten definiert

const FeeCalculator = ({ basePrice, setBasePrice, newFee, setNewFee, showNewFee, setShowNewFee }) => {
  // Referenz für den Container der Gebührenübersicht-Kacheln
  const tilesContainerRef = useRef(null);
  
  // Funktion zum Exportieren der Gebührenübersicht als PNG mit transparentem Hintergrund
  const exportFeeTilesToPNG = async () => {
    if (tilesContainerRef.current) {
      try {
        // Konfiguration für html2canvas
        const options = {
          backgroundColor: null, // Transparenter Hintergrund
          scale: 2, // Höhere Qualität
          logging: false,
          useCORS: true, // Erlaubt das Laden von Cross-Origin-Bildern
        };
        
        // Erstellen des Canvas-Elements
        const canvas = await html2canvas(tilesContainerRef.current, options);
        
        // Konvertieren zu Daten-URL
        const dataUrl = canvas.toDataURL('image/png');
        
        // Erstellen eines Download-Links
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = 'NIGHTOWL_Gebuehrenvergleich.png';
        
        // Link anklicken und entfernen
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Fehler beim Exportieren der Gebührenübersicht:', error);
        alert('Beim Exportieren ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
      }
    }
  };  // State für die berechneten Gebühren
  const [currentFee, setCurrentFee] = useState({ percentage_fee: 0, fixed_fee: 0 });
  const [competitors, setCompetitors] = useState([]);
  const [cheapestFee, setCheapestFee] = useState(null);
  const [allFees, setAllFees] = useState([]);   const [paymentMethods, setPaymentMethods] = useState([]);  // Neue States für die Warenkorb-Werte
  const [ticketCount, setTicketCount] = useState(1.74);
  const [cartValue, setCartValue] = useState(28.47);
  
  // States für die Sperrfunktionen der einzelnen Schieberegler
  const [ticketPriceLocked, setTicketPriceLocked] = useState(false);
  const [ticketCountLocked, setTicketCountLocked] = useState(false);
  const [cartValueLocked, setCartValueLocked] = useState(false);  // States für die Eventinformationen
  const [ticketsPerEvent, setTicketsPerEvent] = useState(173.84);
  const [ticketsPerEventLocked, setTicketsPerEventLocked] = useState(false);
  const [ticketsPerMonth, setTicketsPerMonth] = useState(6326.25);
  const [ticketsPerMonthLocked, setTicketsPerMonthLocked] = useState(false);
  const [eventsPerOrganizer, setEventsPerOrganizer] = useState(4.46);
  const [eventsPerOrganizerLocked, setEventsPerOrganizerLocked] = useState(false);
    // States für die Zahlungsmethoden
  const [distributions, setDistributions] = useState([]);
  const [totalDistribution, setTotalDistribution] = useState(0);  const [distributionLocks, setDistributionLocks] = useState({});  // Neue State für die Sperrfunktionen der Verteilungen
    // State für Payout-Konditionen
  const [payoutConditions, setPayoutConditions] = useState({
    percentage_fee: 0.25,
    fixed_fee: 0.10,
    active_account_fee: 2.00
  });
  
  // Verwende Debounce für die Werte, um zu häufige Updates zu vermeiden
  const debouncedNewFee = useDebounce(newFee, 100);
  const debouncedBasePrice = useDebounce(basePrice, 100);
  const debouncedTicketCount = useDebounce(ticketCount, 100);
  const debouncedCartValue = useDebounce(cartValue, 100);
  const debouncedTicketsPerEvent = useDebounce(ticketsPerEvent, 100);
  const debouncedTicketsPerMonth = useDebounce(ticketsPerMonth, 100);
  const debouncedEventsPerOrganizer = useDebounce(eventsPerOrganizer, 100);
  // Laden der aktuellen Gebühren und Wettbewerber beim Seitenaufruf  
useEffect(() => {
  // Wartestatus für jede Datenquelle
  let feesLoaded = false;
  let competitorsLoaded = false;
  let paymentMethodsLoaded = false;
  let payoutConditionsLoaded = false;

  // Funktion zum Überprüfen, ob alle Daten geladen wurden und die Gebühren neu berechnet werden können
  const checkAllLoaded = () => {
    console.log(`Daten geladen: Gebühren=${feesLoaded}, Wettbewerber=${competitorsLoaded}, Zahlungsmethoden=${paymentMethodsLoaded}, Auszahlungsbedingungen=${payoutConditionsLoaded}`);
    if (feesLoaded && competitorsLoaded && paymentMethodsLoaded && payoutConditionsLoaded) {
      console.log('Alle Daten geladen, berechne Gebühren neu');
      setTimeout(() => calculateAllFees(), 10);
    }
  };

  // Aktuelle Gebühren laden
  ApiService.getCurrentFee()
    .then(data => {
      if (data) {
        setCurrentFee({
          percentage_fee: data.percentage_fee,
          fixed_fee: data.fixed_fee
        });
      }
      feesLoaded = true;
      checkAllLoaded();
    })
    .catch(error => {
      console.error('Fehler beim Laden der aktuellen Gebühren:', error);
      feesLoaded = true;
      checkAllLoaded();
    });
  
  // Payout-Konditionen laden
  ApiService.getPayoutConditions()
    .then(data => {
      if (data && data.length > 0) {
        setPayoutConditions({
          percentage_fee: data[0].percentage_fee,
          fixed_fee: data[0].fixed_fee,
          active_account_fee: data[0].active_account_fee || 1.00
        });
      }
      payoutConditionsLoaded = true;
      checkAllLoaded();
    })
    .catch(error => {
      console.error('Fehler beim Laden der Payout-Konditionen:', error);
      payoutConditionsLoaded = true;
      checkAllLoaded();
    });
  
  // Wettbewerber laden
  ApiService.getCompetitors()
    .then(data => {
      if (data && Array.isArray(data)) {
        console.log('Wettbewerber geladen:', data);
        // Für jeden Wettbewerber die Pakete laden, aber einzelne Fehler abfangen
        const competitorPromises = data.map(comp => 
          ApiService.getCompetitorById(comp.id)
            .catch(error => {
              console.error(`Fehler beim Laden des Wettbewerbers ${comp.id}:`, error);
              // Einfachen Wettbewerber ohne Pakete zurückgeben
              return comp;
            })
        );
        
        Promise.all(competitorPromises)
          .then(detailedCompetitors => {
            console.log('Detaillierte Wettbewerber geladen:', detailedCompetitors);
            const validCompetitors = detailedCompetitors.filter(comp => comp !== null);
            setCompetitors(validCompetitors);
            competitorsLoaded = true;
            checkAllLoaded();
          })
          .catch(error => {
            console.error('Allgemeiner Fehler beim Laden der Wettbewerber:', error);
            // Im Fehlerfall zumindest die grundlegenden Wettbewerber anzeigen
            setCompetitors(data);
            competitorsLoaded = true;
            checkAllLoaded();
          });
      } else {
        console.log('Keine Wettbewerber gefunden oder ungültiges Datenformat:', data);
        setCompetitors([]);
        competitorsLoaded = true;
        checkAllLoaded();
      }
    })
    .catch(error => {
      console.error('Fehler beim Laden der Wettbewerber:', error);
      setCompetitors([]);
      competitorsLoaded = true;
      checkAllLoaded();
    });

  // Zahlungsmethoden direkt laden und für Berechnungen verfügbar machen
  ApiService.getPaymentMethods()
    .then(data => {
      if (data) {
        setPaymentMethods(data);
      }
      paymentMethodsLoaded = true;
      checkAllLoaded();
    })
    .catch(error => {
      console.error('Fehler beim Laden der Zahlungsmethoden:', error);
      paymentMethodsLoaded = true;
      checkAllLoaded();
    });
}, []);
  
  // Laden der gespeicherten Warenkorb- und Allgemeines-Werte
  useEffect(() => {
    // Warenkorb-Werte laden
    const savedBasePrice = localStorage.getItem('basePrice');
    if (savedBasePrice !== null) {
      setBasePrice(parseFloat(savedBasePrice));
    }
    
    const savedTicketCount = localStorage.getItem('ticketCount');
    if (savedTicketCount !== null) {
      setTicketCount(parseFloat(savedTicketCount));
    }
    
    const savedCartValue = localStorage.getItem('cartValue');
    if (savedCartValue !== null) {
      setCartValue(parseFloat(savedCartValue));
    }
    
    // Locks für Warenkorb laden
    const savedTicketPriceLocked = localStorage.getItem('ticketPriceLocked');
    if (savedTicketPriceLocked !== null) {
      setTicketPriceLocked(JSON.parse(savedTicketPriceLocked));
    }
    
    const savedTicketCountLocked = localStorage.getItem('ticketCountLocked');
    if (savedTicketCountLocked !== null) {
      setTicketCountLocked(JSON.parse(savedTicketCountLocked));
    }
    
    const savedCartValueLocked = localStorage.getItem('cartValueLocked');
    if (savedCartValueLocked !== null) {
      setCartValueLocked(JSON.parse(savedCartValueLocked));
    }
    
    // Allgemeines-Werte laden
    const savedTicketsPerEvent = localStorage.getItem('ticketsPerEvent');
    if (savedTicketsPerEvent !== null) {
      setTicketsPerEvent(parseFloat(savedTicketsPerEvent));
    }
    
    const savedTicketsPerMonth = localStorage.getItem('ticketsPerMonth');
    if (savedTicketsPerMonth !== null) {
      setTicketsPerMonth(parseFloat(savedTicketsPerMonth));
    }
    
    const savedEventsPerOrganizer = localStorage.getItem('eventsPerOrganizer');
    if (savedEventsPerOrganizer !== null) {
      setEventsPerOrganizer(parseFloat(savedEventsPerOrganizer));
    }
    
    // Locks für Allgemeines laden
    const savedTicketsPerEventLocked = localStorage.getItem('ticketsPerEventLocked');
    if (savedTicketsPerEventLocked !== null) {
      setTicketsPerEventLocked(JSON.parse(savedTicketsPerEventLocked));
    }
    
    const savedTicketsPerMonthLocked = localStorage.getItem('ticketsPerMonthLocked');
    if (savedTicketsPerMonthLocked !== null) {
      setTicketsPerMonthLocked(JSON.parse(savedTicketsPerMonthLocked));
    }
    
    const savedEventsPerOrganizerLocked = localStorage.getItem('eventsPerOrganizerLocked');    if (savedEventsPerOrganizerLocked !== null) {
      setEventsPerOrganizerLocked(JSON.parse(savedEventsPerOrganizerLocked));
    }
  }, []);
    // Laden der Verteilungen in einem separaten useEffect
  useEffect(() => {
    // Prüfen, ob gespeicherte Verteilungen existieren
    const savedDistributions = localStorage.getItem('distributions');
    
    // Zahlungsmethoden laden
    ApiService.getPaymentMethods()
      .then(data => {
        let dist;
        
        // Wenn gespeicherte Verteilungen existieren, diese verwenden
        if (savedDistributions !== null) {
          const parsedSavedDist = JSON.parse(savedDistributions);
          // Merge der aktuellen Daten mit den gespeicherten Einstellungen
          dist = data.map(method => {
            const savedDist = parsedSavedDist.find(d => d.id === method.id);
            return {
              id: method.id,
              name: method.name,              percentage: savedDist ? Number(savedDist.percentage) : Number(method.distribution_percentage || 0),
              isSelected: savedDist ? savedDist.isSelected : (method.distribution_percentage > 0),
              percentage_fee: Number(method.percentage_fee || 0),
              fixed_fee: Number(method.fixed_fee || 0)
            };
          });        } else {
          // Wenn keine gespeicherten Verteilungen existieren, die Werte aus den Zahlungsmethoden verwenden
          dist = data.map(method => {
            // Distribution Percentage aus der Methode selbst verwenden
            const defaultPercentage = Number(method.distribution_percentage || 0);
            // Methoden mit Verteilungsprozentsatz > 0 sind standardmäßig ausgewählt
            const isSelected = defaultPercentage > 0;
            
            return {
              id: method.id,
              name: method.name,
              percentage: defaultPercentage,
              isSelected: isSelected,
              percentage_fee: Number(method.percentage_fee || 0),
              fixed_fee: Number(method.fixed_fee || 0)
            };
          });
        }
          setDistributions(dist);
        
        // Gesamtsumme berechnen
        const total = dist.reduce((sum, item) => 
          sum + (item.isSelected ? parseFloat(item.percentage || 0) : 0), 0);
        setTotalDistribution(total);
        
        // Wenn keine gespeicherten Verteilungen existieren, die neuen Standard-Werte im localStorage speichern
        if (savedDistributions === null) {
          localStorage.setItem('distributions', JSON.stringify(dist));
        }
      })
      .catch(error => {
        console.error('Fehler beim Laden der Zahlungsmethoden:', error);
      });
  }, []);  
  
  // Berechne alle Gebühren neu, wenn sich die debounced Werte ändern  
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    calculateAllFees();  }, [    debouncedBasePrice, competitors, currentFee, debouncedNewFee, showNewFee, 
    distributions, debouncedCartValue, debouncedTicketCount,
    debouncedTicketsPerEvent, debouncedTicketsPerMonth, debouncedEventsPerOrganizer
  ]);
    // Berechnung der eigenen Gebühr
  const calculateOwnFee = (avgPaymentFeePerTicket) => {
    // Gesamter Warenkorbwert
    const totalCartValue = basePrice * ticketCount;
    
    // Prozentuale Gebühr auf den gesamten Warenkorbwert berechnen
    const percentagePart = totalCartValue * (currentFee.percentage_fee / 100);
    
    // Fixe Gebühr wird mit der Anzahl der Tickets multipliziert
    const fixedFee = currentFee.fixed_fee * ticketCount;
    
    // Gesamtgebühr für den Warenkorb
    const totalCartFee = percentagePart + fixedFee;
    
    // Gebühr pro Ticket berechnen
    const feePerTicket = totalCartFee / ticketCount;
    
    // Tatsächliche Marge pro Ticket berechnen (NIGHTOWL-Gebühr pro Ticket minus Ø Paymentgebühr und Payout-Gebühr)
    const payoutFeePerTicket = calculatePayoutFeePerTicket().payoutFeePerTicket;
    const netMarginPerTicket = feePerTicket - avgPaymentFeePerTicket - payoutFeePerTicket;
      // Gesamtpreis pro Ticket berechnen
    const totalPrice = parseFloat(basePrice) + feePerTicket;
      return {
      id: 'own',
      name: 'NIGHTOWL',
      packageName: 'Aktuelle Gebühr',
      percentageFee: (percentagePart / ticketCount).toFixed(2), // Pro Ticket anzeigen
      fixedFee: (fixedFee / ticketCount).toFixed(2), // Pro Ticket anzeigen
      totalFee: feePerTicket.toFixed(2),
      totalPrice: totalPrice.toFixed(2),      percentage: currentFee.percentage_fee,
      paymentFeePerTicket: avgPaymentFeePerTicket.toFixed(2),
      payoutFeePerTicket: payoutFeePerTicket.toFixed(2),
      netMarginPerTicket: netMarginPerTicket.toFixed(2),
      // Zusätzliche Werte für die detaillierte Berechnung
      totalCartValue: totalCartValue.toFixed(2),
      totalCartFee: totalCartFee.toFixed(2)
    };
  };    // Berechnung der neuen NIGHTOWL Gebühr
  const calculateNewFee = (avgPaymentFeePerTicket) => {
    // Gesamter Warenkorbwert
    const totalCartValue = basePrice * ticketCount;
    
    // Prozentuale Gebühr auf den gesamten Warenkorbwert berechnen
    const percentagePart = totalCartValue * (newFee.percentage_fee / 100);
      // Fixe Gebühr wird mit der Anzahl der Tickets multipliziert
    const fixedFee = newFee.fixed_fee * ticketCount;
    
    // Gesamtgebühr für den Warenkorb
    const totalCartFee = percentagePart + fixedFee;
      // Gebühr pro Ticket berechnen
    const feePerTicket = totalCartFee / ticketCount;
    
    // Tatsächliche Marge pro Ticket berechnen (NIGHTOWL-Gebühr pro Ticket minus Ø Paymentgebühr und Payout-Gebühr)
    const payoutFeePerTicket = calculatePayoutFeePerTicket().payoutFeePerTicket;
    const netMarginPerTicket = feePerTicket - avgPaymentFeePerTicket - payoutFeePerTicket;
    
    // Gesamtpreis pro Ticket berechnen
    const totalPrice = parseFloat(basePrice) + feePerTicket;
      return {
      id: 'new',
      name: 'NIGHTOWL',
      packageName: 'Neue Gebühr',
      percentageFee: (percentagePart / ticketCount).toFixed(2), // Pro Ticket anzeigen
      fixedFee: (fixedFee / ticketCount).toFixed(2), // Pro Ticket anzeigen
      totalFee: feePerTicket.toFixed(2),
      totalPrice: totalPrice.toFixed(2),      percentage: newFee.percentage_fee,
      paymentFeePerTicket: avgPaymentFeePerTicket.toFixed(2),
      payoutFeePerTicket: payoutFeePerTicket.toFixed(2),
      netMarginPerTicket: netMarginPerTicket.toFixed(2),
      // Zusätzliche Werte für die detaillierte Berechnung
      totalCartValue: totalCartValue.toFixed(2),
      totalCartFee: totalCartFee.toFixed(2)
    };
  };  // Berechnung der Gebühr für ein bestimmtes Paket eines Wettbewerbers
  const calculatePackageFee = (competitor, pkg) => {
    // Prüfen, ob das Paket auf den aktuellen Basispreis anwendbar ist
    const isApplicable = 
      (!pkg.min_ticket_price && !pkg.max_ticket_price) ||
      (pkg.min_ticket_price && !pkg.max_ticket_price && basePrice >= pkg.min_ticket_price) ||
      (!pkg.min_ticket_price && pkg.max_ticket_price && basePrice <= pkg.max_ticket_price) ||
      (pkg.min_ticket_price && pkg.max_ticket_price && basePrice >= pkg.min_ticket_price && basePrice <= pkg.max_ticket_price);
    
    if (!isApplicable) return null;
    
    // Für die Vergleichskacheln: Berechnung direkt pro Ticket (nicht auf Warenkorbebene)
    let totalFee;
    
    if (pkg.is_flatrate) {
      // Bei Flatrate wird die Gebühr direkt auf den Ticketpreis angewendet
      totalFee = pkg.flatrate_amount;
    } else {
      // Prozentuale Gebühr auf den Ticketpreis anwenden
      const percentagePart = basePrice * (pkg.percentage_fee / 100);
      // Fixe Gebühr hinzufügen
      const fixedPart = pkg.fixed_fee;
      totalFee = percentagePart + fixedPart;
    }
      // Mehrwertsteuer berücksichtigen (19%)
    const VAT_RATE = 0.19;
    
    // MwSt berechnen und Gebühren-Werte für die Anzeige vorbereiten
    let percentageFeeValue = pkg.is_flatrate ? 0 : (basePrice * (pkg.percentage_fee / 100));
    let fixedFeeValue = pkg.is_flatrate ? pkg.flatrate_amount : pkg.fixed_fee;
    
    // Wenn die Gebühren ohne MwSt. angegeben sind, müssen wir die MwSt. für alle Werte hinzufügen
    if (pkg.is_tax_inclusive === false) {
      totalFee = totalFee * (1 + VAT_RATE);
      percentageFeeValue = percentageFeeValue * (1 + VAT_RATE);
      fixedFeeValue = fixedFeeValue * (1 + VAT_RATE);
    }
    
    // Gesamtpreis pro Ticket berechnen
    const totalPrice = parseFloat(basePrice) + totalFee;return {      id: `${competitor.id}-${pkg.id}`,
      name: competitor.name,
      logo_path: competitor.logo_path,
      packageName: pkg.name,
      isFlatrate: pkg.is_flatrate,
      percentageFee: percentageFeeValue.toFixed(2),
      fixedFee: fixedFeeValue.toFixed(2),
      totalFee: totalFee.toFixed(2),
      totalPrice: totalPrice.toFixed(2),
      percentage: pkg.is_flatrate ? 0 : pkg.percentage_fee,
      percentageWithVAT: pkg.is_flatrate ? 0 : (pkg.is_tax_inclusive === false ? pkg.percentage_fee * 1.19 : pkg.percentage_fee),
      is_tax_inclusive: pkg.is_tax_inclusive // Steuerstatus weitergeben
    };
  };  // Berechne alle Gebühren und sortiere sie
  const calculateAllFees = useCallback(() => {
    // Zuerst berechnen wir NIGHTOWL-Gebühren ohne Payment-Gebühr
    const ownFeeWithoutPayment = calculateOwnFee(0);
    const newCalculatedFeeWithoutPayment = calculateNewFee(0);

    // Dann berechnen wir die Paymentgebühren auf Basis der Gesamtbeträge (Warenkorbwert + NIGHTOWL-Gebühr)
    const paymentFeeDataOwn = calculateAveragePaymentFeeForTotal(
      parseFloat(ownFeeWithoutPayment.totalCartValue) + parseFloat(ownFeeWithoutPayment.totalCartFee)
    );
    const paymentFeeDataNew = calculateAveragePaymentFeeForTotal(
      parseFloat(newCalculatedFeeWithoutPayment.totalCartValue) + parseFloat(newCalculatedFeeWithoutPayment.totalCartFee)
    );
    
    // Finale Berechnung der NIGHTOWL-Gebühren mit den korrekten Paymentgebühren
    const ownFee = calculateOwnFee(paymentFeeDataOwn.fee); 
    const newCalculatedFee = calculateNewFee(paymentFeeDataNew.fee); // Immer berechnen, unabhängig von showNewFee
      const competitorFees = competitors
      .filter(competitor => competitor && competitor.id) // Stellen sicher, dass nur gültige Wettbewerber verarbeitet werden
      .flatMap(competitor => 
      competitor.packages && Array.isArray(competitor.packages)
        ? competitor.packages
            .map(pkg => calculatePackageFee(competitor, pkg))
            .filter(fee => fee !== null) // Filtere nicht anwendbare Pakete
        : []
    );

    const fees = [ownFee, newCalculatedFee, ...competitorFees].filter(fee => fee !== null);
    
    // Sortiere nach Preis (günstigste zuerst)
    const sortedFees = fees.sort((a, b) => 
      parseFloat(a.totalFee) - parseFloat(b.totalFee)
    );
      // Bestimme die günstigste Gebühr
    if (sortedFees.length > 0) {
      setCheapestFee(sortedFees[0].id);
    }    setAllFees(sortedFees);
  // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [basePrice, ticketCount, newFee, currentFee, competitors, payoutConditions]);  
    // Berechnung der Ø Paymentgebühren basierend auf einem Gesamtwert
  const calculateAveragePaymentFeeForTotal = (totalAmount) => {
    // Prüfen, ob Distributions-Array vorhanden ist
    if (!distributions || distributions.length === 0) {
      console.log('Keine Zahlungsmethoden geladen, verwende Standardwerte');
      // Standardwerte zurückgeben, wenn keine Distributions geladen wurden
      return { fee: 0, resultingPercentageFee: 1.5, resultingFixedFee: 0.35 };
    }

    // Nur ausgewählte und mit Prozentsätzen versehene Zahlungsmethoden berücksichtigen
    const selectedPaymentMethods = distributions.filter(item => 
      item.isSelected && item.percentage > 0
    );
    
    // Wenn keine Zahlungsmethoden ausgewählt sind, Standardwerte zurückgeben
    if (selectedPaymentMethods.length === 0) return { fee: 0, resultingPercentageFee: 1.5, resultingFixedFee: 0.35 };
    
    // Gesamtprozentsatz der ausgewählten Zahlungsmethoden berechnen
    const totalPercentage = selectedPaymentMethods.reduce(
      (sum, item) => sum + parseFloat(item.percentage || 0), 
      0
    );
    
    // Wenn der Gesamtprozentsatz 0 ist, 0 zurückgeben
    if (totalPercentage === 0) return { fee: 0, resultingPercentageFee: 0, resultingFixedFee: 0 };
    
    // Gewichteten resultierenden Prozentsatz und fixe Gebühr berechnen
    let resultingPercentageFee = 0;
    let resultingFixedFee = 0;
    
    // Berechnen des gewichteten resultierenden Prozentsatzes und der fixen Gebühr
    for (const method of selectedPaymentMethods) {
      // Normalisiertes Gewicht dieser Zahlungsmethode (tatsächlicher Anteil an 100%)
      const normalizedWeight = method.percentage / totalPercentage;
      
      // Aufaddieren des gewichteten Prozentsatzes und der fixen Gebühr
      resultingPercentageFee += method.percentage_fee * normalizedWeight;
      resultingFixedFee += method.fixed_fee * normalizedWeight;
    }
    
    // Berechnung der Payment-Gebühr für den gesamten Betrag (Warenkorb + NIGHTOWL-Gebühr)
    const percentagePart = totalAmount * (resultingPercentageFee / 100);
    const totalPaymentFee = percentagePart + resultingFixedFee;
    
    // Paymentgebühr pro Ticket berechnen (durch Anzahl der Tickets teilen)
    const paymentFeePerTicket = ticketCount > 0 ? totalPaymentFee / ticketCount : 0;
    
    return {
      fee: roundToTwoDecimals(paymentFeePerTicket),
      resultingPercentageFee: roundToTwoDecimals(resultingPercentageFee),
      resultingFixedFee: roundToTwoDecimals(resultingFixedFee)
    };
  };
  
  // Berechnung der Ø Paymentgebühren pro Ticket basierend auf der Verteilung
  const calculateAveragePaymentFeePerTicket = () => {
    // Diese Funktion ist für Abwärtskompatibilität und die Anzeige der resultierenden Gebühren
    return calculateAveragePaymentFeeForTotal(cartValue);
  };
    // Berechnung der Payoutgebühr pro Ticket
  const calculatePayoutFeePerTicket = () => {
    // Anzahl Veranstalter pro Monat berechnen
    const organizersPerMonth = eventsPerOrganizer > 0 && ticketsPerEvent > 0 
      ? (ticketsPerMonth / ticketsPerEvent) / eventsPerOrganizer
      : 0;
    
    // Gebühr pro aktivem Account berechnen (Gebühr × Anzahl Veranstalter pro Monat)
    const totalActiveAccountFee = payoutConditions.active_account_fee * organizersPerMonth;
    
    // Anzahl Verkäufe pro Monat berechnen
    const salesPerMonth = ticketCount > 0 ? ticketsPerMonth / ticketCount : 0;
    
    // Berechnung der aktiven Account Gebühr pro Ticket
    const activeAccountFeePerTicket = salesPerMonth > 0 ? totalActiveAccountFee / salesPerMonth : 0;

    // Anzahl Events pro Monat berechnen
    const eventsPerMonth = ticketsPerEvent > 0 ? ticketsPerMonth / ticketsPerEvent : 0;
    
    // Fixe monatliche Gebühr berechnen (Events pro Monat * 0,10€)
    const fixedMonthlyFee = eventsPerMonth * 0.10;
    
    // Durchschnittliches Gesamtvolumen berechnen
    // (Durchschnittliche Tickets * Events pro Monat * (Ticketpreis + NIGHTOWL-Gebühr))
    const feePerTicket = allFees.find(fee => fee.id === 'new')?.totalFee || 0;
    const totalVolume = ticketsPerEvent * eventsPerMonth * (basePrice + parseFloat(feePerTicket));
    
    // Prozentbasierte monatliche Gebühr berechnen
    // (Gesamtvolumen * Prozentsatz der Payout-Gebühr)
    const percentageMonthlyFee = totalVolume * (payoutConditions.percentage_fee / 100);
    
    // Gesamtgebühr pro Monat
    const totalMonthlyFee = fixedMonthlyFee + percentageMonthlyFee;
    
    // Payout-Gebühr pro Ticket berechnen
    const newPayoutFeePerTicket = ticketsPerMonth > 0 ? totalMonthlyFee / ticketsPerMonth : 0;
    
    // Gesamte Payout-Gebühr pro Ticket = aktive Account Gebühr + neue Payout-Gebühr
    const payoutFeePerTicket = activeAccountFeePerTicket + newPayoutFeePerTicket;
      
    return {
      organizersPerMonth: organizersPerMonth, // Kein Runden mehr
      totalActiveAccountFee: roundToTwoDecimals(totalActiveAccountFee),
      salesPerMonth: salesPerMonth, // Kein Runden mehr
      eventsPerMonth: eventsPerMonth,
      fixedMonthlyFee: roundToTwoDecimals(fixedMonthlyFee),
      percentageMonthlyFee: roundToTwoDecimals(percentageMonthlyFee),
      activeAccountFeePerTicket: roundToTwoDecimals(activeAccountFeePerTicket),
      newPayoutFeePerTicket: roundToTwoDecimals(newPayoutFeePerTicket),
      payoutFeePerTicket: roundToTwoDecimals(payoutFeePerTicket)
    };
  };  const handleBasePriceChange = (event, newValue) => {
    setBasePrice(newValue);
    localStorage.setItem('basePrice', newValue);
    
    // Wenn der Warenkorb nicht gesperrt ist, aktualisiere ihn basierend auf Ticketwert und -anzahl
    if (!cartValueLocked) {
      const newCartValue = roundToTwoDecimals(newValue * ticketCount);
      setCartValue(newCartValue);
      localStorage.setItem('cartValue', newCartValue);
    } else if (!ticketCountLocked && cartValue > 0) {
      // Wenn Warenkorb gesperrt und Ticketanzahl nicht gesperrt ist, aktualisiere die Ticketanzahl
      const newTicketCount = roundToTwoDecimals(cartValue / newValue);
      setTicketCount(newTicketCount);
      localStorage.setItem('ticketCount', newTicketCount);
    }
  };
  
  const handleBasePriceInputChange = (event) => {
    // Wenn das Feld leer ist, setze 0
    const value = event.target.value === '' ? 0 : parseFloat(event.target.value);
    const newValue = value < 0 ? 0 : value;
    setBasePrice(newValue);
    localStorage.setItem('basePrice', newValue);
    
    // Wenn der Warenkorb nicht gesperrt ist, aktualisiere ihn basierend auf Ticketwert und -anzahl
    if (!cartValueLocked) {
      const newCartValue = roundToTwoDecimals(newValue * ticketCount);
      setCartValue(newCartValue);
      localStorage.setItem('cartValue', newCartValue);
    } else if (!ticketCountLocked && cartValue > 0 && newValue > 0) {
      // Wenn Warenkorb gesperrt und Ticketanzahl nicht gesperrt ist, aktualisiere die Ticketanzahl
      const newTicketCount = roundToTwoDecimals(cartValue / newValue);
      setTicketCount(newTicketCount);
      localStorage.setItem('ticketCount', newTicketCount);
    }
  };
  
  const handlePaymentMethodToggle = (id) => () => {
    setDistributions(prev => {
      const updated = prev.map(item => 
        item.id === id ? { ...item, isSelected: !item.isSelected } : item
      );
      
      // Berechne Gesamtsumme neu
      const total = updated.reduce((sum, item) => 
        sum + (item.isSelected ? parseFloat(item.percentage || 0) : 0), 0);
      setTotalDistribution(total);
      
      // Speichere aktualisierte Verteilungen im localStorage
      localStorage.setItem('distributions', JSON.stringify(updated));
      
      return updated;
    });
  };
  
  // Funktion zur Anpassung der Verteilungswerte, damit die Summe 100% ergibt
  const adjustDistributionsTo100Percent = (distributions, changedId, newValue) => {
    // Kopie der Verteilungen erstellen
    const updatedDistributions = [...distributions];
    
    // Index des geänderten Elements finden
    const changedIndex = updatedDistributions.findIndex(d => d.id === changedId);
    if (changedIndex === -1) return updatedDistributions;
    
    // Den neuen Wert für das geänderte Element setzen (max 100)
    updatedDistributions[changedIndex].percentage = Math.min(newValue, 100);
    
    // Alle ausgewählten Elemente (außer dem geänderten) ermitteln
    const selectedDistributions = updatedDistributions.filter(
      d => d.isSelected && d.id !== changedId && !distributionLocks[d.id]
    );
    
    // Wenn es keine anderen ausgewählten und ungesperrten Elemente gibt, kann keine Anpassung vorgenommen werden
    if (selectedDistributions.length === 0) return updatedDistributions;
    
    // Aktuelle Summe aller Verteilungen berechnen
    let currentTotal = updatedDistributions.reduce(
      (sum, item) => sum + (item.isSelected ? parseFloat(item.percentage || 0) : 0), 
      0
    );
    
    // Wenn die Summe bereits 100% ist oder alle anderen Werte gesperrt sind, keine Anpassung vornehmen
    if (Math.abs(currentTotal - 100) < 0.01) return updatedDistributions;
    
    // Differenz zum Zielwert von 100%
    const difference = 100 - currentTotal;
    
    // Summe der aktuellen Werte der ausgewählten und nicht gesperrten Elemente
    const sumOfSelected = selectedDistributions.reduce(
      (sum, item) => sum + parseFloat(item.percentage || 0), 
      0
    );
    
    // Wenn die Summe der ausgewählten Elemente 0 ist, gleichmäßig verteilen
    if (sumOfSelected === 0 && difference > 0) {
      const equalShare = difference / selectedDistributions.length;
      selectedDistributions.forEach(dist => {
        const index = updatedDistributions.findIndex(d => d.id === dist.id);
        updatedDistributions[index].percentage = equalShare;
      });
      return updatedDistributions;
    }
    
    // Verhältnismäßige Anpassung der anderen ausgewählten Elemente
    selectedDistributions.forEach(dist => {
      const index = updatedDistributions.findIndex(d => d.id === dist.id);
      if (sumOfSelected === 0) {
        // Bei Summe 0 gleichmäßig verteilen
        updatedDistributions[index].percentage = difference / selectedDistributions.length;
      } else {
        // Proportionale Anpassung
        const proportion = dist.percentage / sumOfSelected;
        const adjustment = difference * proportion;
        // Stellen sicher, dass keine negativen Werte entstehen
        updatedDistributions[index].percentage = Math.max(0, dist.percentage + adjustment);
      }
    });
    
    return updatedDistributions;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '1400px', mx: 'auto' }}>
      <Typography variant="h4" align="center" gutterBottom>
        Ticketing Gebührenrechner
      </Typography>
      
      <Grid container sx={{ mb: 4 }}>
        {/* Linke Seite: Neue NIGHTOWL Gebühr und Basisticketpreis */}
        <Grid item xs={12} md={8.4} sx={{ 
          pr: { md: 2 },          width: { md: '70%' }
        }}>          <Box>
              {/* Neue NIGHTOWL Gebühr */}
            <Accordion defaultExpanded elevation={3} sx={{ mb: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="neue-nightowl-gebuhr-content"
                id="neue-nightowl-gebuhr-header"
                sx={{ minHeight: "48px" }}
              >
                <Typography variant="h6">
                  Neue NIGHTOWL Gebühr
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <SliderContainer>                  <Box sx={{ mb: 3 }}>
                    <Typography gutterBottom align="left">
                      Prozentuale Gebühr: {newFee.percentage_fee}%
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>                      <Slider
                        value={newFee.percentage_fee}
                        onChange={(e, newValue) => {
                          const updatedFee = { ...newFee, percentage_fee: newValue };
                          setNewFee(updatedFee);
                          localStorage.setItem('newFee', JSON.stringify(updatedFee));
                        }}
                        min={0.10}
                        max={15.00}
                        step={0.1}
                        valueLabelDisplay="auto"
                        sx={{ flexGrow: 1 }}
                      />                      <NumberInputField
                        value={newFee.percentage_fee}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          const newValue = Math.max(0.10, Math.min(15.00, value));
                          const updatedFee = { ...newFee, percentage_fee: newValue };
                          setNewFee(updatedFee);
                          localStorage.setItem('newFee', JSON.stringify(updatedFee));
                        }}
                        size="small"
                        min={0.10}
                        max={15.00}
                        step={0.1}
                        endAdornment="%"
                        sx={{ width: 100 }}
                      />
                    </Box>
                  </Box>                  <Box>
                    <Typography gutterBottom align="left">
                      Fixe Gebühr: {newFee.fixed_fee} €
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>                      <Slider
                        value={newFee.fixed_fee}
                        onChange={(e, newValue) => {
                          const updatedFee = { ...newFee, fixed_fee: newValue };
                          setNewFee(updatedFee);
                          localStorage.setItem('newFee', JSON.stringify(updatedFee));
                        }}
                        min={0}
                        max={10}
                        step={0.01}
                        valueLabelDisplay="auto"
                        sx={{ flexGrow: 1 }}
                      />                      <NumberInputField
                        value={newFee.fixed_fee}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          const newValue = value < 0 ? 0 : value;
                          const updatedFee = { ...newFee, fixed_fee: newValue };
                          setNewFee(updatedFee);
                          localStorage.setItem('newFee', JSON.stringify(updatedFee));
                        }}
                        size="small"
                        min={0}
                        step={0.01}
                        endAdornment="€"
                        sx={{ width: 100 }}
                      />
                    </Box>
                  </Box>
                </SliderContainer>
              </AccordionDetails>
            </Accordion>
              {/* Basisticketpreis - direkt unter den neuen Gebühren */}
            <Accordion defaultExpanded elevation={3} sx={{ mb: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="warenkorb-content"
                id="warenkorb-header"
                sx={{ minHeight: "48px" }}
              >
                <Typography variant="h6">
                  Warenkorb
                </Typography>
              </AccordionSummary>              <AccordionDetails sx={{ p: 0 }}>
                <SliderContainer>                  {/* Ø Ticketnennpreis mit Sperrfunktion */}
                  <Box sx={{ mb: 3 }}>
                    <Typography gutterBottom align="left">
                      Ø Ticketnennpreis: {basePrice.toFixed(2)} €
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Slider
                        value={basePrice}
                        onChange={handleBasePriceChange}
                        aria-labelledby="base-price-slider"
                        min={0}
                        max={100}
                        step={0.01}
                        disabled={ticketPriceLocked}
                        sx={{ flexGrow: 1 }}
                      />
                        <NumberInputField
                        value={basePrice}
                        onChange={handleBasePriceInputChange}
                        size="small"
                        disabled={ticketPriceLocked}
                        min={0}
                        step={0.01}
                        sx={{ width: 100 }}
                      /><IconButton 
                        onClick={() => {
                          const newValue = !ticketPriceLocked;
                          setTicketPriceLocked(newValue);
                          localStorage.setItem('ticketPriceLocked', JSON.stringify(newValue));
                        }} 
                        size="small"
                        sx={{ 
                          color: ticketPriceLocked ? 'var(--primary-main)' : 'inherit',
                        }}
                      >
                        {ticketPriceLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>                    {/* Ø Ticketanzahl mit Sperrfunktion */}
                  <Box sx={{ mb: 3 }}>
                    <Typography gutterBottom align="left">
                      Ø Ticketanzahl: {ticketCount.toFixed(2)} Tickets
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>                      <Slider
                        value={ticketCount}
                        onChange={(e, newValue) => {
                          setTicketCount(newValue);
                          localStorage.setItem('ticketCount', newValue);
                          if (!cartValueLocked) {
                            const newCartValue = roundToTwoDecimals(newValue * basePrice);
                            setCartValue(newCartValue);
                            localStorage.setItem('cartValue', newCartValue);
                          }
                        }}
                        aria-labelledby="ticket-count-slider"
                        min={1}
                        max={10}
                        step={0.01}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => value.toFixed(2)}
                        disabled={ticketCountLocked}
                        sx={{ flexGrow: 1 }}
                      />                        <NumberInputField
                        value={ticketCount}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 1 : parseFloat(e.target.value);                        
                          const newValue = value < 1 ? 1 : value;
                          setTicketCount(newValue);
                          localStorage.setItem('ticketCount', newValue);
                          if (!cartValueLocked) {
                            const newCartValue = roundToTwoDecimals(newValue * basePrice);
                            setCartValue(newCartValue);
                            localStorage.setItem('cartValue', newCartValue);
                          }
                        }}
                        size="small"
                        disabled={ticketCountLocked}
                        min={1}
                        step={0.01}
                        sx={{ width: 100 }}
                      /><IconButton 
                        onClick={() => {
                          const newValue = !ticketCountLocked;
                          setTicketCountLocked(newValue);
                          localStorage.setItem('ticketCountLocked', JSON.stringify(newValue));
                        }} 
                        size="small"
                        sx={{ 
                          color: ticketCountLocked ? 'var(--primary-main)' : 'inherit',
                        }}
                      >
                        {ticketCountLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>                  {/* Ø Warenkorbwert mit Sperrfunktion */}
                  <Box>
                    <Typography gutterBottom align="left">
                      Ø Warenkorbwert: {cartValue.toFixed(2)} €
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>                      <Slider
                        value={cartValue}
                        onChange={(e, newValue) => {
                          const newCartValue = roundToTwoDecimals(newValue);
                          setCartValue(newCartValue);
                          localStorage.setItem('cartValue', newCartValue);
                          if (!ticketCountLocked && basePrice > 0) {
                            const newTicketCount = roundToTwoDecimals(newValue / basePrice);
                            setTicketCount(newTicketCount);
                            localStorage.setItem('ticketCount', newTicketCount);
                          }
                        }}
                        aria-labelledby="cart-value-slider"
                        min={0}
                        max={1000}
                        step={0.01}
                        disabled={cartValueLocked}
                        sx={{ flexGrow: 1 }}
                      />                        <NumberInputField
                        value={cartValue}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          const newValue = value < 0 ? 0 : value;
                          const newCartValue = roundToTwoDecimals(newValue);
                          setCartValue(newCartValue);
                          localStorage.setItem('cartValue', newCartValue);
                          if (!ticketCountLocked && basePrice > 0) {
                            const newTicketCount = roundToTwoDecimals(newValue / basePrice);
                            setTicketCount(newTicketCount);
                            localStorage.setItem('ticketCount', newTicketCount);
                          }
                        }}
                        size="small"
                        disabled={cartValueLocked}
                        min={0}
                        step={0.01}
                        endAdornment="€"
                        sx={{ width: 100 }}
                      /><IconButton 
                        onClick={() => {
                          const newValue = !cartValueLocked;
                          setCartValueLocked(newValue);
                          localStorage.setItem('cartValueLocked', JSON.stringify(newValue));
                        }}
                        size="small"
                        sx={{ 
                          color: cartValueLocked ? 'var(--primary-main)' : 'inherit',
                        }}
                      >
                        {cartValueLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>
                </SliderContainer>
              </AccordionDetails>
            </Accordion>
              {/* Neue Paymentmethoden-Kachel */}
            <Accordion defaultExpanded elevation={3} sx={{ mt: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="paymentmethoden-content"
                id="paymentmethoden-header"
                sx={{ minHeight: "48px" }}
              >
                <Typography variant="h6">
                  Paymentmethoden
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <SliderContainer>
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="body1" fontWeight="bold" color="text.primary" gutterBottom>
                      Resultierende Paymentgebühr: {Number(calculateAveragePaymentFeePerTicket().resultingPercentageFee || 0).toFixed(2)}% + {Number(calculateAveragePaymentFeePerTicket().resultingFixedFee || 0).toFixed(2)}€
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Wähle die zu verwendenden Zahlungsmethoden und lege deren prozentuale Verteilung fest.
                      Die Summe der ausgewählten Methoden sollte 100% ergeben.
                    </Typography>
                    
                    <List>
                      {distributions.map((distribution) => (
                        <ListItem
                          key={distribution.id}
                          dense
                          divider
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={distribution.isSelected}
                              onChange={handlePaymentMethodToggle(distribution.id)}
                              tabIndex={-1}
                            />
                          </ListItemIcon>                          <CustomListItemText 
                            primary={                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography component="span" variant="body1">{distribution.name}:</Typography>
                                <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                                  [{typeof distribution.percentage_fee === 'number' ? distribution.percentage_fee.toFixed(2) : '0.00'}% + {typeof distribution.fixed_fee === 'number' ? distribution.fixed_fee.toFixed(2) : '0.00'}€]
                                </Typography>
                              </Box>
                            }
                            secondary={
                              distribution.isSelected && (
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ flex: 1, mr: 2 }}>
                                    <Slider
                                      value={distribution.percentage}
                                      onChange={(e, newValue) => {
                                        // Verwende die adjustDistributionsTo100Percent-Funktion
                                        const updatedDistributions = adjustDistributionsTo100Percent(distributions, distribution.id, newValue);
                                        setDistributions(updatedDistributions);
                                          
                                        // Gesamtsumme neu berechnen
                                        const total = updatedDistributions.reduce((sum, item) => 
                                          sum + (item.isSelected ? parseFloat(item.percentage || 0) : 0), 0);
                                        setTotalDistribution(total);
                                        
                                        // Speichere Verteilungen im localStorage
                                        localStorage.setItem('distributions', JSON.stringify(updatedDistributions));
                                      }}
                                      min={0}
                                      max={100}
                                      step={0.01}
                                      valueLabelDisplay="auto"
                                      valueLabelFormat={(value) => value.toFixed(2)}
                                      disabled={distributionLocks[distribution.id]}
                                    />
                                  </Box>                                  <NumberInputField
                                    value={roundToTwoDecimals(distribution.percentage)}
                                    onChange={(e) => {
                                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                      const newValue = Math.max(0, Math.min(100, value)); // Begrenzen zwischen 0 und 100
                                      
                                      // Verwende die adjustDistributionsTo100Percent-Funktion auch für manuelle Eingaben
                                      const updatedDistributions = adjustDistributionsTo100Percent(distributions, distribution.id, newValue);
                                      setDistributions(updatedDistributions);
                                      
                                      // Gesamtsumme neu berechnen
                                      const total = updatedDistributions.reduce((sum, item) => 
                                        sum + (item.isSelected ? parseFloat(item.percentage || 0) : 0), 0);
                                      setTotalDistribution(total);
                                      
                                      // Speichere Verteilungen im localStorage
                                      localStorage.setItem('distributions', JSON.stringify(updatedDistributions));
                                    }}
                                    size="small"
                                    disabled={distributionLocks[distribution.id]}
                                    min={0}
                                    max={100}
                                    step={0.01}
                                    endAdornment="%"
                                    sx={{ width: 100, mr: 1 }}
                                  />
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      setDistributionLocks(prev => {
                                        const updated = {
                                          ...prev,
                                          [distribution.id]: !prev[distribution.id]
                                        };
                                        // Speichere die aktualisierten Locks im localStorage
                                        localStorage.setItem('distributionLocks', JSON.stringify(updated));
                                        return updated;
                                      });
                                    }}
                                    sx={{ 
                                      color: distributionLocks[distribution.id] ? 'var(--primary-main)' : 'inherit',
                                    }}
                                  >
                                    {distributionLocks[distribution.id] ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                                  </IconButton>
                                </Box>
                              )
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                    <Typography variant="subtitle1">
                      Summe:
                    </Typography>
                    <Typography 
                      variant="subtitle1" 
                      color={Math.abs(totalDistribution - 100) < 0.01 ? 'success.main' : 'error.main'}
                      fontWeight="bold"
                    >
                      {totalDistribution.toFixed(2)}%
                    </Typography>
                  </Box>
                  
                  {Math.abs(totalDistribution - 100) > 0.01 && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      Die Summe sollte genau 100% ergeben.
                    </Typography>
                  )}                </SliderContainer>
              </AccordionDetails>
            </Accordion>            {/* Neue Allgemeines-Kachel */}
            <Accordion defaultExpanded elevation={3} sx={{ mt: 3, mb: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="eventinformationen-content"
                id="eventinformationen-header"
                sx={{ minHeight: "48px" }}
              >
                <Typography variant="h6">
                  Allgemeines
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <SliderContainer>
                  {/* Ø Anzahl Tickets pro Event */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>                      <Typography gutterBottom>
                        Ø Anzahl Tickets pro Event: {typeof ticketsPerEvent === 'number' ? ticketsPerEvent.toFixed(2) : ticketsPerEvent}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>                      <Slider
                        value={ticketsPerEvent}
                        onChange={(e, newValue) => {
                          setTicketsPerEvent(newValue);
                          localStorage.setItem('ticketsPerEvent', newValue);
                        }}
                        aria-labelledby="tickets-per-event-slider"
                        min={10}
                        max={500}
                        step={0.01}
                        valueLabelDisplay="auto"
                        disabled={ticketsPerEventLocked}
                        sx={{ flexGrow: 1 }}
                      />                        <NumberInputField
                        value={typeof ticketsPerEvent === 'number' ? ticketsPerEvent : parseFloat(ticketsPerEvent) || 0}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setTicketsPerEvent(''); // Erlaube leeres Feld während der Eingabe
                          } else {
                            const numVal = parseFloat(val);
                            const newValue = isNaN(numVal) ? ticketsPerEvent : numVal;
                            setTicketsPerEvent(newValue);
                            if (typeof newValue === 'number') {
                              localStorage.setItem('ticketsPerEvent', newValue);
                            }
                          }
                        }}
                        onBlur={() => {
                          let val = parseFloat(ticketsPerEvent);
                          if (isNaN(val) || val === '') val = 10; // Standardwert oder alter Wert
                          val = Math.max(10, val); // Min-Wert sicherstellen
                          setTicketsPerEvent(val);
                        }}
                        size="small"
                        disabled={ticketsPerEventLocked}
                        min={10}
                        step={0.01}
                        sx={{ width: 100 }}
                      /><IconButton 
                        onClick={() => {
                          const newValue = !ticketsPerEventLocked;
                          setTicketsPerEventLocked(newValue);
                          localStorage.setItem('ticketsPerEventLocked', JSON.stringify(newValue));
                        }} 
                        size="small"
                        sx={{ 
                          color: ticketsPerEventLocked ? 'var(--primary-main)' : 'inherit',
                        }}
                      >
                        {ticketsPerEventLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {/* Anzahl Tickets pro Monat */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography gutterBottom>
                        Anzahl Tickets pro Monat: {typeof ticketsPerMonth === 'number' ? ticketsPerMonth.toFixed(0) : ticketsPerMonth}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>                      <Slider
                        value={ticketsPerMonth}
                        onChange={(e, newValue) => {
                          setTicketsPerMonth(newValue);
                          localStorage.setItem('ticketsPerMonth', newValue);
                        }}
                        aria-labelledby="tickets-per-month-slider"
                        min={1}
                        max={100000}
                        step={10}
                        valueLabelDisplay="auto"
                        disabled={ticketsPerMonthLocked}
                        sx={{ flexGrow: 1 }}
                      />                      <NumberInputField
                        value={typeof ticketsPerMonth === 'number' ? ticketsPerMonth : parseFloat(ticketsPerMonth) || 0}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setTicketsPerMonth(''); // Erlaube leeres Feld während der Eingabe
                          } else {
                            const numVal = parseFloat(val);
                            const newValue = isNaN(numVal) ? ticketsPerMonth : numVal;
                            setTicketsPerMonth(newValue);
                            if (typeof newValue === 'number') {
                              localStorage.setItem('ticketsPerMonth', newValue);
                            }
                          }
                        }}
                        onBlur={() => {
                          let val = parseFloat(ticketsPerMonth);
                          if (isNaN(val) || val === '') val = 1000; // Standardwert oder alter Wert
                          val = Math.max(1, val); // Min-Wert sicherstellen
                          setTicketsPerMonth(val);
                        }}
                        size="small"
                        disabled={ticketsPerMonthLocked}
                        min={1}
                        step={10}
                        sx={{ width: 100 }}
                      /><IconButton 
                        onClick={() => {
                          const newValue = !ticketsPerMonthLocked;
                          setTicketsPerMonthLocked(newValue);
                          localStorage.setItem('ticketsPerMonthLocked', JSON.stringify(newValue));
                        }} 
                        size="small"
                        sx={{ 
                          color: ticketsPerMonthLocked ? 'var(--primary-main)' : 'inherit',
                        }}
                      >
                        {ticketsPerMonthLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                      </IconButton>
                    </Box>                  </Box>
                  
                  {/* Events pro Veranstalter mit Payout */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography gutterBottom>
                        Events pro Veranstalter mit Payout: {typeof eventsPerOrganizer === 'number' ? eventsPerOrganizer.toFixed(2) : eventsPerOrganizer}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>                      <Slider
                        value={eventsPerOrganizer}
                        onChange={(e, newValue) => {
                          setEventsPerOrganizer(newValue);
                          localStorage.setItem('eventsPerOrganizer', newValue);
                        }}
                        aria-labelledby="events-per-organizer-slider"
                        min={0}
                        max={10}
                        step={0.01}
                        valueLabelDisplay="auto"
                        disabled={eventsPerOrganizerLocked}
                        sx={{ flexGrow: 1 }}
                      />                      <NumberInputField
                        value={typeof eventsPerOrganizer === 'number' ? eventsPerOrganizer : parseFloat(eventsPerOrganizer) || 0}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setEventsPerOrganizer('');
                          } else {
                            const numVal = parseFloat(val);
                            const newValue = isNaN(numVal) ? eventsPerOrganizer : numVal;
                            setEventsPerOrganizer(newValue);
                            if (typeof newValue === 'number') {
                              localStorage.setItem('eventsPerOrganizer', newValue);
                            }
                          }
                        }}
                        onBlur={() => {
                          let val = parseFloat(eventsPerOrganizer);
                          if (isNaN(val) || val === '') val = 2; // Standardwert oder alter Wert
                          val = Math.max(0, val); // Min-Wert sicherstellen
                          setEventsPerOrganizer(val);
                        }}
                        size="small"
                        disabled={eventsPerOrganizerLocked}
                        min={0}
                        step={0.01}
                        sx={{ width: 100 }}
                      /><IconButton 
                        onClick={() => {
                          const newValue = !eventsPerOrganizerLocked;
                          setEventsPerOrganizerLocked(newValue);
                          localStorage.setItem('eventsPerOrganizerLocked', JSON.stringify(newValue));
                        }} 
                        size="small"
                        sx={{ 
                          color: eventsPerOrganizerLocked ? 'var(--primary-main)' : 'inherit',
                        }}
                      >
                        {eventsPerOrganizerLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>
                </SliderContainer>
              </AccordionDetails>            </Accordion>
          </Box>
        </Grid>
          {/* Rechte Seite: Ergebnisse */}        <Grid item xs={12} md={3.6} sx={{
          width: { md: '30%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          marginTop: { xs: 0, md: 0 }, /* Margin entfernt, damit es mit den Accordion-Elementen bündig ist */
        }}>          <Paper elevation={3} sx={{ 
            height: 'auto',
            display: 'flex', 
            flexDirection: 'column',
            mt: 0, /* Kein margin-top */
            mb: { xs: 3, md: 0 },
            width: '100%' /* Volle Breite des übergeordneten Containers */
          }}>
            <SliderContainer sx={{ 
              padding: 2,
              flexGrow: 0,
              marginTop: 0, /* Inneres margin-top entfernt */
              marginBottom: 0 /* Inneres margin-bottom entfernt */
            }}>
              <Typography variant="h6" gutterBottom>
                Ergebnisse
              </Typography>
              
              <Box sx={{ mt: 2 }}>                {allFees.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    
                    {allFees.some(fee => fee.id === 'own') && (
                      <>
                        <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ color: 'text.primary' }}>
                          NIGHTOWL aktuelle Gebühr:
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          Gebühr/Ticket: {allFees.find(fee => fee.id === 'own')?.totalFee} €
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          Endpreis/Ticket: {allFees.find(fee => fee.id === 'own')?.totalPrice} €
                        </Typography>
                        
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          Gebühr/Warenkorb: {(parseFloat(allFees.find(fee => fee.id === 'own')?.totalFee) * ticketCount).toFixed(2)} €
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          Gesamtwarenkorbwert: {(parseFloat(allFees.find(fee => fee.id === 'own')?.totalPrice) * ticketCount).toFixed(2)} €
                        </Typography>

                        <Divider sx={{ my: 1 }} />                        <Typography variant="body2" color="text.secondary">
                          Paymentgebühr/Ticket: {allFees.find(fee => fee.id === 'own')?.paymentFeePerTicket} €
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Paymentgebühr/Warenkorb: {(parseFloat(allFees.find(fee => fee.id === 'own')?.paymentFeePerTicket) * ticketCount).toFixed(2)} €
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Payoutgebühr/Ticket: {allFees.find(fee => fee.id === 'own')?.payoutFeePerTicket} €
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Payoutgebühr/Warenkorb: {(parseFloat(allFees.find(fee => fee.id === 'own')?.payoutFeePerTicket) * ticketCount).toFixed(2)} €
                        </Typography>
                        <Typography 
                          variant="body1" 
                          fontWeight="bold" 
                          color={parseFloat(allFees.find(fee => fee.id === 'own')?.netMarginPerTicket) > 0 ? "success.main" : "error.main"}
                        >
                          Netto-Marge/Ticket: {allFees.find(fee => fee.id === 'own')?.netMarginPerTicket} €
                        </Typography>
                        
                        <Divider sx={{ my: 2 }} />
                      </>
                    )}
                    
                    {allFees.some(fee => fee.id === 'new') && (
                      <>
                        <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ color: 'var(--primary-main)' }}>
                          NIGHTOWL neue Gebühr:
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          Gebühr/Ticket: {allFees.find(fee => fee.id === 'new')?.totalFee} €
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          Endpreis/Ticket: {allFees.find(fee => fee.id === 'new')?.totalPrice} €
                        </Typography>
                        
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          Gebühr/Warenkorb: {(parseFloat(allFees.find(fee => fee.id === 'new')?.totalFee) * ticketCount).toFixed(2)} €
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          Gesamtwarenkorbwert: {(parseFloat(allFees.find(fee => fee.id === 'new')?.totalPrice) * ticketCount).toFixed(2)} €
                        </Typography>

                        <Divider sx={{ my: 1 }} />                        <Typography variant="body2" color="text.secondary">
                          Paymentgebühr/Ticket: {allFees.find(fee => fee.id === 'new')?.paymentFeePerTicket} €
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Paymentgebühr/Warenkorb: {(parseFloat(allFees.find(fee => fee.id === 'new')?.paymentFeePerTicket) * ticketCount).toFixed(2)} €
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Payoutgebühr/Ticket: {allFees.find(fee => fee.id === 'new')?.payoutFeePerTicket} €
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Payoutgebühr/Warenkorb: {(parseFloat(allFees.find(fee => fee.id === 'new')?.payoutFeePerTicket) * ticketCount).toFixed(2)} €
                        </Typography>
                        <Typography 
                          variant="body1" 
                          fontWeight="bold" 
                          color={parseFloat(allFees.find(fee => fee.id === 'new')?.netMarginPerTicket) > 0 ? "success.main" : "error.main"}
                        >
                          Netto-Marge/Ticket: {allFees.find(fee => fee.id === 'new')?.netMarginPerTicket} €
                        </Typography>
                      </>
                    )}
                  </>
                )}              </Box>            </SliderContainer>
          </Paper>
          
          {/* Debug-Kachel für Payoutgebühren */}
          <Paper elevation={3} sx={{ 
            height: 'auto',
            display: 'flex', 
            flexDirection: 'column',
            mt: 2,
            mb: 2,
            width: '100%',
            backgroundColor: theme => theme.palette.background.default
          }}>
            <SliderContainer sx={{ 
              padding: 2,
              flexGrow: 0,
              marginTop: 0,
              marginBottom: 0
            }}>
              <Typography variant="h6" gutterBottom color="primary">
                Debug: Payoutgebühr-Berechnung
              </Typography>
                <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Eingangswerte:
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Gebühr pro aktivem Account: {payoutConditions.active_account_fee} €
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Fixed Fee pro Event: 0.10 €
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Prozentuale Payout-Gebühr: {payoutConditions.percentage_fee} %
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Ø Tickets pro Event: {ticketsPerEvent}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Tickets pro Monat: {ticketsPerMonth}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Ø Ticketanzahl pro Verkauf: {ticketCount}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Events pro Veranstalter: {eventsPerOrganizer}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Berechnete Werte:
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Anzahl Veranstalter pro Monat: {calculatePayoutFeePerTicket().organizersPerMonth.toFixed(2)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Anzahl Events pro Monat: {calculatePayoutFeePerTicket().eventsPerMonth.toFixed(2)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Anzahl Verkäufe pro Monat: {calculatePayoutFeePerTicket().salesPerMonth.toFixed(2)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Fixe monatliche Gebühr: {calculatePayoutFeePerTicket().fixedMonthlyFee} €
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Prozentbasierte monatliche Gebühr: {calculatePayoutFeePerTicket().percentageMonthlyFee} €
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Gebühr pro aktivem Account gesamt: {calculatePayoutFeePerTicket().totalActiveAccountFee} €
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Active Account Gebühr pro Ticket: {calculatePayoutFeePerTicket().activeAccountFeePerTicket} €
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Neue Payout-Gebühr pro Ticket: {calculatePayoutFeePerTicket().newPayoutFeePerTicket} €
                </Typography>
                <Typography variant="body2" color="primary" fontWeight="bold" gutterBottom>
                  Gesamte Payoutgebühr pro Ticket: {calculatePayoutFeePerTicket().payoutFeePerTicket} €
                </Typography>
              </Box>
            </SliderContainer>
          </Paper>
        </Grid>
      </Grid>      <Box display="flex" alignItems="center" justifyContent="space-between" mt={4} mb={2}>
        <Typography variant="h5">
          Gebührenübersicht
        </Typography>
        <Tooltip title="Als PNG exportieren">
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<DownloadIcon />}
            onClick={exportFeeTilesToPNG}
            size="small"
          >
            Exportieren
          </Button>
        </Tooltip>
      </Box>
      
      <TilesContainer ref={tilesContainerRef}>
        {allFees.map((fee) => {
          const isCheapest = fee.id === cheapestFee;
          const isNightowl = fee.id === 'own' || fee.id === 'new';
          
          return (
            <TileWrapper 
              key={fee.id}
            >              {isCheapest && (
                <Box sx={{ 
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  backgroundColor: 'var(--success)',
                  color: 'var(--neutral-100)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                  zIndex: 1
                }}>
                  <LocalOfferIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.75rem' }} />
                  Günstigster
                </Box>
              )}
              
              <FeeTile isCheapest={isCheapest} isNightowl={isNightowl} isOwnNightowl={fee.id === 'own'}>
                <TileContent>
                  <LogoContainer isCheapest={isCheapest} isNightowl={isNightowl}>
                    {fee.id === 'own' || fee.id === 'new' ? (
                      <Logo 
                        src="/images/Nightowl.png" 
                        alt="NIGHTOWL Logo"
                      />                    ) : fee.logo_path ? (
                      <Logo 
                        src={fee.logo_path} 
                        alt={`${fee.name} Logo`}
                      />
                    ) : (
                      <Typography variant="h6" fontWeight="bold">
                        {fee.name}
                      </Typography>
                    )}
                  </LogoContainer>
                  <PackageName variant="body1" isNightowl={isNightowl}>
                    {fee.packageName}
                  </PackageName>
                  <PriceContainer variant="h5" isCheapest={isCheapest}>
                    {fee.totalFee}€
                  </PriceContainer>
                  <DetailsContainer>                    {fee.isFlatrate ? (
                      <Typography variant="body2" component="p" textAlign="center">
                        Flatrate-Gebühr: {fee.fixedFee} €
                      </Typography>
                    ) : (
                      <Typography variant="body2" component="p" textAlign="center">
                        {fee.percentageWithVAT !== undefined ? fee.percentageWithVAT.toFixed(1) : fee.percentage}% + {fee.fixedFee} €
                      </Typography>
                    )}<Typography variant="body2" component="p" textAlign="center" color="text.secondary">
                      Inkl. 19% MwSt.
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" component="p" fontWeight="bold" textAlign="center">
                      Gesamtpreis: {fee.totalPrice} €
                    </Typography>
                  </DetailsContainer>
                </TileContent>
              </FeeTile>
            </TileWrapper>
          );
        })}      </TilesContainer>
    </Box>
  );
};

export default FeeCalculator;